import { useSuspenseQueries } from '@tanstack/react-query';
import { MICROBIOME_AGE_CATEGORY_KEY, getKitSciVersion } from '@repo/utils';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useAnalyticsSource } from 'contexts/analytics/useAnalyticsSource';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { getActionPlanItemsQuery } from 'services/actionPlan/actionPlanData';
import useFeature from 'services/features/useFeature';
import useIsGutResilienceScoreEnabled from 'services/features/useIsGutResilienceScoreEnabled';
import { getSubMetricDetailQuery } from 'services/insights/useSubMetricDetail';
import { getTaxaOverTimeQuery } from 'services/insights/useTaxaOverTime';
import { MATURATION_INDEX_CATEGORY_KEY } from 'views/Results/Summary/SummaryCards/MatIndexCard/MatIndexCard';
import { DefaultSubMetricDetail } from './DefaultSubMetricDetail';
import {
  GutResilienceScoreDetail,
  RESILIENCE_CATEGORY_KEY,
} from './GutResilienceScoreDetail';
import { MaturationIndexDetail } from './MaturationIndexDetail';
import MicrobiomeAgeDetail from './MicrobiomeAgeDetail';

export const SubMetricDetail = () => {
  const { kitId, subMetricId } = useParams<{
    kitId: string;
    subMetricId: string;
  }>();
  const { currentKit } = useCurrentKitAndTinyAccount();
  const sciVersion = getKitSciVersion(currentKit);
  const [{ data: subMetric }, { data: allActions }] = useSuspenseQueries({
    queries: [
      getSubMetricDetailQuery(kitId, subMetricId, sciVersion),
      getActionPlanItemsQuery(kitId),
      getTaxaOverTimeQuery(kitId, parseInt(subMetricId ?? '')),
    ],
  });

  const { trackEvent } = useAnalytics();
  const metricSource = useAnalyticsSource<
    'all_insights' | 'main_finding' | 'conditions' | 'direct'
  >();
  const { isEnabled: isGutResilienceScoreEnabled } =
    useIsGutResilienceScoreEnabled();
  useEffect(() => {
    if (!subMetric?.name) return;
    trackEvent('Metric Viewed', {
      metric_name: subMetric.name,
      metric_category: subMetric.category_key,
      source: metricSource,
    });
  }, [subMetric?.name, subMetric?.category_key, metricSource, trackEvent]);

  const { isFeatureEnabled: isMicrobiomeAgeEnabled } =
    useFeature('microbiome_age');

  if (subMetric.category_key === MATURATION_INDEX_CATEGORY_KEY) {
    //For any maturation index, we want to show the MaturationIndexDetail component instead
    return <MaturationIndexDetail />;
  }

  if (
    subMetric.category_key === MICROBIOME_AGE_CATEGORY_KEY &&
    isMicrobiomeAgeEnabled
  ) {
    return (
      <MicrobiomeAgeDetail submetric={subMetric} allActions={allActions} />
    );
  }

  if (
    isGutResilienceScoreEnabled &&
    subMetric.category_key === RESILIENCE_CATEGORY_KEY
  ) {
    return <GutResilienceScoreDetail />;
  }

  return (
    <DefaultSubMetricDetail subMetric={subMetric} allActions={allActions} />
  );
};
